<script setup lang="ts">
type StudentUserData = App.Auth.Data.StudentUserData;
import type { Selectable } from '@/components/selectBox/selectBox';

import { ButtonColor, ButtonSize, ButtonVariant } from '@/hooks/useButtonClasses';

import Button from '@/components/button/Button.vue';
import DangerDialog from '@/components/dialog/DangerDialog.vue';
import Dialog from '@/components/dialog/Dialog.vue';
import FormListboxInput from '@/components/formListboxInput/FormListboxInput.vue';
import FormRows from '@/components/formRows/FormRows.vue';
import FormTextInput from '@/components/formTextInput/FormTextInput.vue';

const props = defineProps<{
  user: StudentUserData;
  channelTypes: App.Base.Data.EnumData[];
}>();

const { show, close } = useDialog();
const [isCancelDialogOpen, toggleCancelDialog] = useToggle(false);

const isDialogOpen = computed(() => !isCancelDialogOpen.value && show.value);

const form = useForm({
  method: 'POST',
  url: route('student.profile.security.two-factor-authentication-channel.store'),
  fields: {
    type: props.channelTypes?.filter((type) => type.value === 'sms')[0],
    destination: ''
  },
  transform: (fields) => ({
    ...fields,
    type: fields.type?.value
  })
});

const isSms = computed(() => form.fields.type.value === 'sms');

function handleTypeChange(value: Selectable<string> | null) {
  form.fields.type = value as Selectable<string>;
  form.fields.destination = '';
}

function handleToggleCancelDialog() {
  if (form.isDirty) {
    toggleCancelDialog();
    return;
  }
  close();
}

function handleCancelDialogConfirm() {
  toggleCancelDialog();
  close();
}
</script>


					<script lang="ts">
					import __hybridly_layout_0 from '~/resources/layouts/student-parent.vue';
					export default { layout: [__hybridly_layout_0] }
					</script>
					<template>
				
  <Dialog
    title="Two-factor authentication"
    :isOpen="isDialogOpen"
    hideFooter
    isStacked
    @onClose="handleToggleCancelDialog"
  >
    <template #form>
      <div class="flex flex-col gap-y-8">
        <p class="text-md leading-relaxed text-slate-700">
          Select a device type and enter your phone number or email address to receive a
          verification code.
        </p>
        <FormRows>
          <FormListboxInput
            label="Device type"
            name="type"
            :options="channelTypes ?? []"
            :error="form.errors.type as string"
            :modelValue="form.fields.type"
            @update:modelValue="handleTypeChange"
          />
          <FormTextInput
            required
            :label="isSms ? 'Phone number' : 'Email address'"
            :placeholder="isSms ? 'Enter your phone number' : 'Enter your email address'"
            name="destination"
            :type="isSms ? 'tel' : 'email'"
            :error="form.errors.destination"
            :modelValue="form.fields.destination"
            @update:modelValue="(value) => (form.fields.destination = value as string)"
          />
        </FormRows>
        <Button
          :color="ButtonColor.primary"
          :isDisabled="!form.fields.destination"
          :size="ButtonSize.lg"
          :variant="ButtonVariant.solid"
          @click="form.submit"
        >
          Send verification code
        </Button>
      </div>
    </template>
  </Dialog>
  <DangerDialog
    title="Discard Changes?"
    message="Your changes will not be saved."
    :isOpen="isCancelDialogOpen"
    cancelButtonLabel="Stay on this page"
    confirmButtonLabel="Discard changes"
    isStacked
    @onCancel="toggleCancelDialog"
    @onClose="toggleCancelDialog"
    @onConfirm="handleCancelDialogConfirm"
  />
</template>
